'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'

import { fetchToolbarLinks, TOOLBAR_PATH_MAP } from '@/src/constants/bottomToolbar'
import { getActiveToolbarTab } from '@/src/utils/navigation/getActiveToolbarTab'

import { BottomToolbar } from './bottomToolbar'

interface BottomToolbarWrapperProps {
    showVisa?: boolean
    showFlightsHotels?: boolean
    topSlot?: React.ReactNode
}

const BottomToolbarWrapper: React.FC<BottomToolbarWrapperProps> = ({
    showVisa,
    showFlightsHotels,
    topSlot
}) => {
    const router = useRouter()
    const pathname = usePathname()
    const t = useTranslations('common')
    const locale = useLocale()

    // Initialize with active tab based on current URL
    const [currentValue, setCurrentValue] = useState<string>(() => getActiveToolbarTab(pathname))

    // Update active tab when pathname changes
    useEffect(() => {
        const activeTab = getActiveToolbarTab(pathname)
        setCurrentValue(activeTab)
    }, [pathname])

    const handleNavigationChange = useCallback(
        (value: string) => {
            const basePath = TOOLBAR_PATH_MAP[value]
            const noLocaleRoutes = ['visa']
            if (!basePath) return

            const path = noLocaleRoutes.includes(value) ? basePath : `/${locale}${basePath}`

            // Hotels is an internal route now, so it falls through to router.push below.
            // fph has no route in this app and stays a hard navigation to the legacy site.
            const externalRoutes: Record<string, string> = {
                fph: `https://www.flyin.com/${locale}/flights-hotels`
            }

            if (externalRoutes[value]) {
                window.location.href = externalRoutes[value]
            } else {
                router.push(path)
            }
        },
        [router, locale]
    )
    const toolbarLinks = useMemo(
        () => fetchToolbarLinks({ showVisa, showFlightsHotels }),
        [showVisa, showFlightsHotels]
    )
    // Translate toolbar links
    const translatedToolbarLinks = useMemo(
        () =>
            toolbarLinks.map(link => ({
                ...link,
                label: t(link.label)
            })),
        [t, locale]
    )

    return (
        <BottomToolbar
            currentValue={currentValue}
            toolbarLinks={translatedToolbarLinks}
            onNavigationChange={handleNavigationChange}
            topSlot={topSlot}
        />
    )
}

export { BottomToolbarWrapper }
export default BottomToolbarWrapper
